import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type {
  ApiResponse,
  BusinessRecordReference,
  CreatePayRunRequest,
  PayGroupReference,
  PayPeriodReference,
  PayRunDetailDto,
  PayRunCalculationSummaryDto,
  PayRunFieldError,
  PayRunListQuery,
  PayRunListResult,
  PayRunType,
  UpdatePayRunRequest,
} from './payRunsApiTypes';

export class PayRunRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors: PayRunFieldError[],
  ) {
    super(message);
    this.name = 'PayRunRequestError';
  }
}

const base = () => `${getApiBaseUrl()}/api/payrun`;

const parse = async <T>(response: Response): Promise<T> => {
  let body: ApiResponse<T>;
  try {
    body = await response.json() as ApiResponse<T>;
  } catch {
    throw new PayRunRequestError(response.status, 'PAYRUN_INTERNAL_ERROR', `Request failed: ${response.status}`, []);
  }

  if (!response.ok || !body.success || body.result == null) {
    throw new PayRunRequestError(
      response.status,
      body.error?.code ?? (response.status === 403 ? 'PAYRUN_FORBIDDEN' : 'PAYRUN_INTERNAL_ERROR'),
      body.message || `Request failed: ${response.status}`,
      body.error?.fieldErrors ?? [],
    );
  }

  return body.result;
};

const setOptional = (params: URLSearchParams, key: string, value?: string) => {
  if (value?.trim()) params.set(key, value.trim());
};

export const listPayRuns = async (
  query: PayRunListQuery,
  signal?: AbortSignal,
): Promise<PayRunListResult> => {
  const params = new URLSearchParams({
    scope: query.scope,
    page: String(query.page),
    pageSize: String(query.pageSize),
    sort: query.sort,
  });
  setOptional(params, 'search', query.search);
  setOptional(params, 'businessRecordId', query.businessRecordId);
  setOptional(params, 'payGroupId', query.payGroupId);
  setOptional(params, 'workflowStatus', query.workflowStatus);
  setOptional(params, 'frequency', query.frequency);
  return parse<PayRunListResult>(await authFetch(`${base()}?${params}`, { signal }));
};

export const listBusinessRecordReferences = async (): Promise<BusinessRecordReference[]> =>
  parse<BusinessRecordReference[]>(await authFetch(`${base()}/reference/business-records`));

export const listPayGroupReferences = async (businessRecordId: string): Promise<PayGroupReference[]> =>
  parse<PayGroupReference[]>(await authFetch(
    `${base()}/reference/pay-groups?${new URLSearchParams({ businessRecordId })}`,
  ));

export const listPayPeriodReferences = async (
  businessRecordId: string,
  payGroupId: string,
  runType: PayRunType,
): Promise<PayPeriodReference[]> =>
  parse<PayPeriodReference[]>(await authFetch(
    `${base()}/reference/pay-periods?${new URLSearchParams({ businessRecordId, payGroupId, runType })}`,
  ));

export const createPayRun = async (
  request: CreatePayRunRequest,
  idempotencyKey: string,
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(base(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(request),
  }));

export const updatePayRun = async (
  payId: string,
  request: UpdatePayRunRequest,
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  }, { retryUnauthorized: false }));

export const transitionPayRun = async (
  payId: string,
  verb: 'make_payroll_active' | 'revert_to_trial' | 'cancel',
): Promise<PayRunDetailDto> =>
  parse<PayRunDetailDto>(await authFetch(`${base()}/${encodeURIComponent(payId)}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verb }),
  }, { retryUnauthorized: false }));

export const startPayRunCalculation = async (
  payId: string,
  idempotencyKey: string,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations`,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
    },
    { retryUnauthorized: false },
  ));

export const getPayRunCalculation = async (
  payId: string,
  attemptId: string,
  signal?: AbortSignal,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations/${encodeURIComponent(attemptId)}`,
    { signal },
  ));

export const retryPayRunCalculation = async (
  payId: string,
  attemptId: string,
): Promise<PayRunCalculationSummaryDto> =>
  parse<PayRunCalculationSummaryDto>(await authFetch(
    `${base()}/${encodeURIComponent(payId)}/calculations/${encodeURIComponent(attemptId)}/retry`,
    { method: 'POST' },
    { retryUnauthorized: false },
  ));
